/**
 * Los resultados de laboratorio del paciente.
 *
 * SOBRE MARCAR LO QUE ESTÁ FUERA DE RANGO
 *
 * Se marca, pero sin rojo de alarma ni iconos de peligro, y con una frase que
 * dice lo único cierto: que un valor fuera de rango no significa por sí solo
 * que algo vaya mal y que lo interpreta el médico. Ocultarlo sería tratar al
 * paciente como un menor de edad —el resultado es suyo—, y dramatizarlo sería
 * mandar a alguien a urgencias por una décima de glucosa.
 */
import { useQuery } from "@tanstack/react-query";
import { Download, FlaskConical } from "lucide-react";
import { type OrdenPortal, descargarResultados, misResultados } from "../../api/portal";
import { usePortalAuth } from "../../auth/PortalAuthProvider";
import { useZonaHoraria } from "../../auth/TenantBrandingProvider";
import { fechaHora } from "../../lib/fecha";
import { Alert, Badge, Card, CardBody } from "../../components/ui";

export function PortalResultadosPage() {
  const { me } = usePortalAuth();
  const tz = useZonaHoraria();
  const { data: ordenes = [], isLoading } = useQuery({
    queryKey: ["portal", "resultados"],
    queryFn: misResultados,
  });

  const hayFueraDeRango = ordenes.some((o) =>
    o.items.some((i) => i.fuera_de_rango),
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Resultados</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Tus exámenes de laboratorio, incluidos los que aún están en proceso.
        </p>
      </div>

      {hayFueraDeRango && (
        <Alert variant="info">
          Algún valor está fuera del rango de referencia. Eso solo significa que
          se sale de lo habitual para la mayoría de las personas: no es un
          diagnóstico. Quien lo interpreta es tu médico, con el resto de tu
          historia.
        </Alert>
      )}

      {isLoading && <p className="text-sm text-slate-400">Cargando…</p>}

      {!isLoading && ordenes.length === 0 && (
        <Card>
          <CardBody className="py-10 text-center text-sm text-slate-500">
            Todavía no tienes exámenes de laboratorio.
          </CardBody>
        </Card>
      )}

      {ordenes.map((o) => (
        <OrdenCard
          key={o.id}
          orden={o}
          tz={tz}
          esOtro={o.paciente_id !== me?.yo.paciente_id}
        />
      ))}
    </div>
  );
}

function OrdenCard({
  orden: o,
  tz,
  esOtro,
}: {
  orden: OrdenPortal;
  tz: string;
  esOtro: boolean;
}) {
  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <FlaskConical className="h-4 w-4 text-[color:var(--color-link)]" />
          <span className="font-semibold text-slate-900">{fechaHora(o.fecha, tz)}</span>
          <Badge variant={o.listo ? "success" : "warning"}>{o.estado_legible}</Badge>
          {esOtro && <Badge variant="neutral">{o.paciente_nombre}</Badge>}
          {o.profesional && (
            <span className="text-sm text-slate-500">· pedido por {o.profesional}</span>
          )}
          {/* Solo cuando hay algo que llevarse: un PDF de "en proceso" no le
              sirve a nadie. */}
          {o.listo && (
            <button
              type="button"
              onClick={() => descargarResultados(o.id)}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-[color:var(--color-link)] hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
            >
              <Download className="h-4 w-4" />
              Descargar
            </button>
          )}
        </div>

        <ul className="divide-y divide-slate-100">
          {o.items.map((i, idx) => (
            <li key={idx} className="py-2">
              <div className="flex flex-wrap items-baseline gap-x-3">
                <span className="font-medium text-slate-900">{i.descripcion}</span>
                {i.tiene_resultado ? (
                  <>
                    {i.valor && (
                      <span
                        className={`font-mono ${
                          i.fuera_de_rango
                            ? "font-semibold text-amber-700"
                            : "text-slate-900"
                        }`}
                      >
                        {i.valor} {i.unidad ?? ""}
                      </span>
                    )}
                    {i.valor_referencia && (
                      <span className="text-xs text-slate-400">
                        referencia: {i.valor_referencia}
                      </span>
                    )}
                    {i.fuera_de_rango && (
                      <span className="text-xs font-medium text-amber-700">
                        fuera del rango habitual
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-sm text-slate-500">En proceso</span>
                )}
              </div>
              {i.texto && (
                <p className="mt-1 whitespace-pre-wrap rounded bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  {i.texto}
                </p>
              )}
            </li>
          ))}
        </ul>

        {o.estado === "CON_RESULTADOS" && (
          <p className="text-sm text-slate-600">
            Puedes recoger el impreso en el laboratorio del policlínico.
          </p>
        )}
      </CardBody>
    </Card>
  );
}
